"use client";

// Primary CTA with static arrow-circle and gradient-background-shadow on pulse.
// White variant keeps the legacy solid surface + animated arrow.

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";

import type { CtaArrowCircleTone } from "@/components/cta-arrow-circle";
import { CtaArrowCircle } from "@/components/cta-arrow-circle";
import { StaticCtaArrowCircle } from "@/components/ui/arrow-cta-button";
import {
  resetBackgroundShadowPosition,
  updateBackgroundShadowPosition,
} from "@/lib/button-light-leak";
import { cn } from "@/lib/utils";

const ctaButtonVariants = cva(
  [
    "group rounded-ds-full inline-flex shrink-0 items-center justify-center font-medium",
    "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
    "active:scale-[0.97] motion-reduce:active:scale-100",
    "disabled:pointer-events-none disabled:cursor-not-allowed",
  ],
  {
    variants: {
      variant: {
        pulse: [
          "button-primary-gradient-background-shadow bg-pulse-700 text-grey-0 disabled:opacity-100",
          "active:scale-100 motion-reduce:active:scale-100",
        ],
        white:
          "bg-grey-0 text-grey-950 hover:bg-grey-50 active:bg-grey-100 focus-visible:ring-grey-300 transition-[background-color,transform] duration-[180ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] disabled:opacity-60",
      },
      size: {
        /** Compact CTA — matches shadcn Button default (38px). */
        default: "text-ds-body-sm h-[38px] gap-2 pr-1.5 pl-4",
        /** Hero/footer pill — matches ArrowPillLink. */
        pill: "text-ds-label-lg gap-3 py-1.5 pr-[7px] pl-4",
        sm: "text-ds-label-md h-8 gap-2 pr-1 pl-3",
        lg: "text-ds-body-sm h-11 gap-3 pr-2 pl-6",
      },
    },
    defaultVariants: {
      variant: "pulse",
      size: "default",
    },
  }
);

function variantToTone(
  variant: "pulse" | "white" | null | undefined
): CtaArrowCircleTone {
  return variant === "white" ? "onWhite" : "onPulse";
}

export interface CtaButtonProps
  extends ButtonPrimitive.Props, VariantProps<typeof ctaButtonVariants> {}

const CtaButton = React.forwardRef<HTMLButtonElement, CtaButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      render,
      nativeButton,
      onPointerEnter,
      onPointerMove,
      onPointerLeave,
      ...props
    },
    ref
  ) => {
    const isPulse = variant !== "white";

    const trackBackgroundShadow = (event: React.PointerEvent<HTMLElement>) => {
      updateBackgroundShadowPosition(
        event.currentTarget,
        event.clientX,
        event.clientY
      );
    };

    return (
      <ButtonPrimitive
        ref={ref}
        data-slot="cta-button"
        className={cn(ctaButtonVariants({ variant, size }), className)}
        nativeButton={nativeButton ?? !render}
        render={render}
        onPointerEnter={
          isPulse
            ? (event) => {
                trackBackgroundShadow(event);
                onPointerEnter?.(event);
              }
            : onPointerEnter
        }
        onPointerMove={
          isPulse
            ? (event) => {
                trackBackgroundShadow(event);
                onPointerMove?.(event);
              }
            : onPointerMove
        }
        onPointerLeave={
          isPulse
            ? (event) => {
                resetBackgroundShadowPosition(event.currentTarget);
                onPointerLeave?.(event);
              }
            : onPointerLeave
        }
        {...props}
      >
        {isPulse ? (
          <span data-slot="button-gradient-label">{children}</span>
        ) : (
          children
        )}
        {isPulse ? (
          <StaticCtaArrowCircle />
        ) : (
          <CtaArrowCircle tone={variantToTone(variant)} />
        )}
      </ButtonPrimitive>
    );
  }
);
CtaButton.displayName = "CtaButton";

export { CtaButton, ctaButtonVariants };
